import { useEffect, useState, useCallback, useRef } from 'react'
import { Platform, Alert, BackHandler, AppState, Linking } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { usePostHog } from 'posthog-react-native'
import { rootDb } from '../global/Firebase/firebase'
import { snapshotExists } from '../global/Firebase/types'
import i18n from '../localization/i18n'
import {
  SOFT_ALERT_STORAGE_KEY,
  isVersionOlderThan,
  shouldShowSoftAlert,
  encodeSoftAlertRecord,
  decodeSoftAlertRecord,
} from '../utils/versionLockout'
// Get version from app.json - use version (not runtimeVersion) to avoid breaking Expo Updates
import appJson from '../app.json'

// Canonical store listings (same ids the marketing site links to, apps/website/src/lib/links.ts).
// The Firestore lockout config (config/app) can override per platform via updateUrlIos /
// updateUrlAndroid — see docs/supabase-migration-docs/ROLLOUT-PLAN.md.
const APP_STORE_URL = 'https://apps.apple.com/app/id1543828442'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=kz.qarasay.kassa'

/** The store listing for this platform, honoring the config override. Null on web. */
export function storeUrlForPlatform(config) {
  if (Platform.OS === 'ios') return config?.updateUrlIos || APP_STORE_URL
  if (Platform.OS === 'android') return config?.updateUrlAndroid || PLAY_STORE_URL
  return null
}

export const APP_VERSION = appJson.expo.version || '0.0.0'

export function useVersionCheck() {
  const [isBlocked, setIsBlocked] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [config, setConfig] = useState(null)
  const postHog = usePostHog()
  // The gate re-evaluates on every foreground; these keep it from re-firing per foreground.
  const softAlertInFlight = useRef(false)
  const hardBlockReported = useRef(false)
  // ATL-969: the gate reported ONLY when it fired, so a gate that blocks nobody was
  // indistinguishable from a gate that never ran. Fleet-wide there were ZERO
  // `%lockout%`/`%version_%` events in the 25 days before this was added — while a hard lockout
  // sat configured and ineffective. Once per JS process, same as the hard-block report.
  const gateEvaluationReported = useRef(false)

  /**
   * ATL-969 — report that the gate RAN, and what it decided, on every outcome including "passed".
   *
   * This exists because the ramp had no denominator. `version_lockout_shown` fires only when the
   * gate BLOCKS, so it can answer "how many were stopped" and never "how many were checked" — and
   * the second is the number you need to decide whether raising `minVersion` is safe. It is also
   * why a hard lockout at 7.0.3 went unnoticed as a no-op: every OTA bundle reports exactly 7.0.3
   * (`expo.version` moves only on a NATIVE store build), so `isVersionOlderThan` was false for the
   * whole fleet and nothing was ever emitted.
   *
   * Reports the no-config and invalid-config paths too — "no config = allow all" silently opens the
   * gate for everybody, which is the single most important thing to be able to see.
   *
   * Purely additive: it changes no blocking decision.
   */
  const reportGateEvaluation = useCallback(
    (outcome, data) => {
      if (gateEvaluationReported.current) return
      gateEvaluationReported.current = true
      postHog?.capture?.('version_gate_evaluated', {
        outcome,
        app_version: APP_VERSION,
        min_version: data?.minVersion ?? null,
        lockout_type: data?.lockoutType ?? null,
        platform: Platform.OS,
      })
    },
    [postHog]
  )

  /**
   * Show the dismissible soft alert at most once per SOFT_ALERT_INTERVAL_MS per minVersion.
   * Returns true when it was actually shown, so the caller can emit telemetry only then.
   */
  const maybeShowSoftAlert = useCallback(async (data) => {
    // Guards the await gap below: two foregrounds in quick succession must not both alert.
    if (softAlertInFlight.current) return false
    softAlertInFlight.current = true
    try {
      const record = decodeSoftAlertRecord(await AsyncStorage.getItem(SOFT_ALERT_STORAGE_KEY))
      const now = Date.now()
      if (!shouldShowSoftAlert({ record, minVersion: data.minVersion, now })) return false
      // Persist BEFORE alerting: the next foreground must see the notice as already given even
      // if this JS process dies while the alert is on screen.
      await AsyncStorage.setItem(
        SOFT_ALERT_STORAGE_KEY,
        encodeSoftAlertRecord({ minVersion: data.minVersion, now })
      )

      // ATL-791: the alert also OFFERS the store install, because for never-OTA runtimes an OTA
      // can never arrive and "OK" alone would leave no path forward when this later flips to hard.
      const storeUrl = storeUrlForPlatform(data)
      const buttons = [{ text: i18n.t('blockedScreen.softDismiss'), style: 'cancel' }]
      if (storeUrl) {
        buttons.push({
          text: i18n.t('blockedScreen.softUpdate'),
          onPress: () => {
            postHog?.capture?.('version_lockout_recovery', {
              method: 'store_opened',
              lockout_type: 'soft',
              min_version: data.minVersion,
              app_version: APP_VERSION,
            })
            Linking.openURL(storeUrl).catch(() => {})
          },
        })
      }
      Alert.alert(
        i18n.t('blockedScreen.softTitle'),
        data.lockoutMessage || i18n.t('blockedScreen.softMessage'),
        buttons
      )
      return true
    } catch (error) {
      // Storage failure must not cost the user the notice.
      console.error('Soft lockout alert throttle failed:', error)
      return false
    } finally {
      softAlertInFlight.current = false
    }
  }, [postHog])

  const checkVersion = useCallback(async () => {
    try {
      // Read config from Firestore
      const configDoc = await rootDb.collection('config').doc('app').get()

      if (!snapshotExists(configDoc)) {
        // No config = allow all
        reportGateEvaluation('no-config', null)
        setIsBlocked(false)
        setIsLoading(false)
        return
      }

      const data = configDoc.data()
      if (!data || !data.minVersion) {
        // Invalid config = allow
        reportGateEvaluation('invalid-config', data)
        setIsBlocked(false)
        setIsLoading(false)
        return
      }

      setConfig(data)

      const isOldVersion = isVersionOlderThan(APP_VERSION, data.minVersion)

      if (isOldVersion && data.lockoutType === 'hard') {
        reportGateEvaluation('blocked-hard', data)
        setIsBlocked(true)
        // Once per JS process, not once per foreground — otherwise a blocked device inflates
        // the very number the ramp is read from.
        if (!hardBlockReported.current) {
          hardBlockReported.current = true
          postHog?.capture?.('version_lockout_shown', {
            lockout_type: 'hard',
            min_version: data.minVersion,
            app_version: APP_VERSION,
          })
        }
      } else if (isOldVersion && data.lockoutType === 'soft') {
        reportGateEvaluation('older-soft', data)
        setIsBlocked(false)
        const shown = await maybeShowSoftAlert(data)
        if (shown) {
          postHog?.capture?.('version_lockout_shown', {
            lockout_type: 'soft',
            min_version: data.minVersion,
            app_version: APP_VERSION,
          })
        }
      } else {
        // Version is OK - unblock. THIS is the branch that was invisible, and the one the ramp's
        // denominator is made of.
        reportGateEvaluation('passed', data)
        setIsBlocked(false)
      }

      setIsLoading(false)
    } catch (error) {
      console.error('Version check failed:', error)
      // A gate that could not run must be visible as such — it is not a pass, and silently
      // treating it as one is how the ramp lost its denominator in the first place.
      reportGateEvaluation('check-failed', null)
      // Don't block app if check fails
      setIsLoading(false)
    }
  }, [maybeShowSoftAlert, postHog, reportGateEvaluation])

  // Check on mount
  useEffect(() => {
    void checkVersion()
  }, [checkVersion])

  // Re-check when app comes back from background
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        void checkVersion()
      }
    })
    return () => subscription.remove()
  }, [checkVersion])

  // Prevent back button when blocked (Android)
  useEffect(() => {
    if (isBlocked && Platform.OS === 'android') {
      const handler = BackHandler.addEventListener('hardwareBackPress', () => true)
      return () => handler.remove()
    }
  }, [isBlocked])

  return { isBlocked, isLoading, config }
}
